// src/pages/admin/AdminCategories.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getCategories, addCategory, updateCategory, deleteCategory } from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [image,      setImage]      = useState("");
  const [saving,     setSaving]     = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    const c = await getCategories();
    setCategories(c);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null); setImage(""); reset({ name: "", description: "" }); setModalOpen(true);
  };
  const openEdit = (cat) => {
    setEditing(cat); setImage(cat.image || ""); reset({ name: cat.name, description: cat.description || "" }); setModalOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data, image };
      if (editing) { await updateCategory(editing.id, payload); toast.success("Category updated"); }
      else         { await addCategory(payload); toast.success("Category added"); }
      setModalOpen(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Products in this category won't be deleted.`)) return;
    await deleteCategory(id);
    toast.success("Category deleted");
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> Add Category
        </button>
      </div>

      {loading ? <Spinner center /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.length === 0 ? (
            <div className="col-span-4 text-center py-16 text-gray-500">
              <p className="text-4xl mb-3">📦</p>
              <p>No categories yet. Add your first one.</p>
            </div>
          ) : (
            categories.map((cat) => (
              <div key={cat.id} className="card p-4 flex flex-col gap-3">
                <div className="aspect-square rounded-xl overflow-hidden bg-primary-50">
                  {cat.image ? (
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">🌿</div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                  {cat.description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{cat.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(cat)} className="btn-secondary btn-sm flex-1 flex items-center justify-center gap-1.5">
                    <FiEdit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(cat.id, cat.name)} className="btn-danger btn-sm flex items-center gap-1.5 px-3">
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Category" : "Add Category"}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Category Name *</label>
            <input {...register("name", { required: "Name is required" })} className={`input ${errors.name ? "border-red-400" : ""}`} placeholder="e.g. Vegetables" />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="label">Description</label>
            <textarea {...register("description")} rows={2} className="input resize-none" placeholder="Brief description..." />
          </div>
          <div>
            <label className="label">Category Image</label>
            <ImageUploader
              folder="categories"
              onUpload={(imgs) => setImage(imgs[0] || "")}
              existingImages={image ? [image] : []}
              maxFiles={1}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update" : "Add Category"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
